import React, { useState, useEffect, useMemo } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Button, TextField, Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton, Typography, Box, TablePagination, InputAdornment, Chip, 
  Card, MenuItem, FormControl, InputLabel, Select
} from '@mui/material';
import { 
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, 
  Search as SearchIcon, Save as SaveIcon 
} from '@mui/icons-material';
import { 
  collection, addDoc, updateDoc, deleteDoc, doc, 
  onSnapshot, query, orderBy, serverTimestamp 
} from "firebase/firestore";
import { db } from "../../firebase"; // Ensure this points to your firebase config

const initialForm = { 
  id: "", 
  category: "", 
  planName: "", 
  premiumAmount: "", 
  coverageAmount: "", 
  duration: "", 
  status: "Active" 
};

export default function InsuranceDetails() {
  const [insurances, setInsurances] = useState([]);
  const [categories, setCategories] = useState([]);
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState(initialForm);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  // 1. FETCH DATA (Insurances & Categories)
  useEffect(() => {
    // Real-time listener for Insurance Records
    const qIns = query(collection(db, "insurances"), orderBy("createdAt", "desc"));
    const unsubIns = onSnapshot(qIns, (snapshot) => {
      setInsurances(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });

    // Real-time listener for Categories (to populate Select Box)
    const qCats = query(collection(db, "insurance_categories"), orderBy("category", "asc"));
    const unsubCats = onSnapshot(qCats, (snapshot) => {
      setCategories(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });

    return () => { unsubIns(); unsubCats(); };
  }, []);

  // 2. SEARCH & FILTER
  const filteredInsurances = useMemo(() => {
    return insurances.filter((item) =>
      item.planName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.category?.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [insurances, searchQuery]);

  // 3. CRUD HANDLERS
  const handleOpen = (data = initialForm) => {
    setFormData(data);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setFormData(initialForm);
  };

  const handleSubmit = async () => {
    try {
      const payload = {
        category: formData.category,
        planName: formData.planName,
        premiumAmount: Number(formData.premiumAmount),
        coverageAmount: Number(formData.coverageAmount),
        duration: Number(formData.duration),
        status: formData.status,
        updatedAt: serverTimestamp()
      };

      if (formData.id) {
        await updateDoc(doc(db, "insurances", formData.id), payload);
      } else {
        await addDoc(collection(db, "insurances"), { ...payload, createdAt: serverTimestamp() });
      }
      handleClose();
    } catch (err) {
      console.error("Firebase Error:", err);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Delete this insurance plan?")) {
      await deleteDoc(doc(db, "insurances", id));
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 1}, margin: '0 auto' }}>
      <Card sx={{ p: 2, borderRadius: 3, boxShadow: "0 4px 20px rgba(0,0,0,0.08)" }}>
        
        {/* HEADER SECTION */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="h6" sx={{ fontWeight: 800, color: '#1a237e' }}>
            Insurance Plans
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField 
              size="small"
              placeholder="Search plan or category..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{ startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} /> }}
            />
            <Button 
              variant="contained" 
              size="small" 
              startIcon={<AddIcon />} 
              onClick={() => handleOpen()}
              sx={{ borderRadius: 2, px: 2 }}
            >
              Add Plan
            </Button>
          </Box>
        </Box>

        {/* DATA TABLE */}
        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #efefef' }}>
          <Table size="small">
            <TableHead sx={{ bgcolor: '#f5f5f5' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Sr.</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Plan Name</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Category</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Premium</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Coverage</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredInsurances.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((ins, index) => (
                <TableRow key={ins.id} hover>
                  <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{ins.planName}</TableCell>
                  <TableCell>{ins.category}</TableCell>
                  <TableCell>₹{ins.premiumAmount?.toLocaleString()}</TableCell>
                  <TableCell>₹{ins.coverageAmount?.toLocaleString()}</TableCell>
                  <TableCell>
                    <Chip 
                      label={ins.status} 
                      size="small" 
                      color={ins.status === 'Active' ? 'success' : 'warning'} 
                      sx={{ fontWeight: 500 }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" color="primary" onClick={() => handleOpen(ins)}><EditIcon fontSize="small" /></IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDelete(ins.id)}><DeleteIcon fontSize="small" /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[5, 10]}
            component="div"
            count={filteredInsurances.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          />
        </TableContainer>
      </Card>

      {/* POPUP DIALOG */}
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 700 }}>
          {formData.id ? "Edit Insurance Plan" : "Create New Plan"}
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, pt: 1 }}>
            
            <TextField 
              label="Plan Name" 
              size="small" 
              fullWidth 
              value={formData.planName} 
              onChange={(e) => setFormData({...formData, planName: e.target.value})} 
            />

            <FormControl fullWidth size="small">
              <InputLabel>Category</InputLabel>
              <Select
                label="Category"
                value={formData.category}
                onChange={(e) => setFormData({...formData, category: e.target.value})}
              >
                {categories.map((cat) => (
                  <MenuItem key={cat.id} value={cat.category}>{cat.category}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField 
              label="Premium Amount" 
              size="small" 
              type="number" 
              value={formData.premiumAmount} 
              onChange={(e) => setFormData({...formData, premiumAmount: e.target.value})} 
            />

            <TextField 
              label="Coverage Amount" 
              size="small" 
              type="number" 
              value={formData.coverageAmount} 
              onChange={(e) => setFormData({...formData, coverageAmount: e.target.value})} 
            />

            <TextField 
              label="Duration (Months)" 
              size="small" 
              type="number" 
              value={formData.duration} 
              onChange={(e) => setFormData({...formData, duration: e.target.value})} 
            />

            <TextField 
              select 
              label="Status" 
              size="small" 
              value={formData.status} 
              onChange={(e) => setFormData({...formData, status: e.target.value})}
            >
              <MenuItem value="Active">Active</MenuItem>
              <MenuItem value="Inactive">Inactive</MenuItem>
              <MenuItem value="Expired">Expired</MenuItem>
            </TextField>

          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={handleClose} color="inherit" size="small">Cancel</Button>
          <Button variant="contained" onClick={handleSubmit} size="small" startIcon={<SaveIcon />}>
            {formData.id ? "Update Plan" : "Save Plan"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}